/* Citizen state store for urban vegetation localization, missions, care logging, and EcoPoints telemetry. */

import { useCallback, useEffect, useState } from 'react'
import type { LocalizedVegetation } from './components/CitizenMap'

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://eco-api-staging.up.railway.app'

export interface CitizenProfileState {
  name: string
  email: string
  role: 'citizen'
  points: number
  level: string
  streakDays: number
  monthlyGoal: number
  completedMissions: number
  treesPlanted: number
  wasteCollectedKg: number
  avatar: string
  notificationPrefs: {
    reminders: boolean
    nearby_missions: boolean
    weekly_email: boolean
  }
}

export interface ClaimedVoucher {
  id: string
  code: string
  partner: string
  offerTitle: string
  pointsCost: number
  claimedAt: string
}

const DEFAULT_PROFILE: CitizenProfileState = {
  name: 'Maya Chen',
  email: 'citizen@econinhursag.ma',
  role: 'citizen',
  points: 1250,
  level: 'Sapling',
  streakDays: 5,
  monthlyGoal: 4,
  completedMissions: 24,
  treesPlanted: 8,
  wasteCollectedKg: 38,
  avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&auto=format&fit=crop&q=80',
  notificationPrefs: {
    reminders: true,
    nearby_missions: true,
    weekly_email: false,
  },
}

const INITIAL_VEGETATION: LocalizedVegetation[] = [
  {
    id: 'veg-01',
    name: 'Sidr Jujube Tree #102',
    species: 'Sidr (Ziziphus spina-christi)',
    species_ar: 'سدر',
    lat: 31.634,
    lng: -7.992,
    district: 'Riverside Walk · Al Haouz',
    health: 'needs-water',
    health_score: 58,
    ndvi: 0.38,
    planted_date: '2024-03-12',
    last_cared_at: '3 days ago',
    photo: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=600&auto=format&fit=crop&q=80',
    adopted_by_me: true,
    mission_title: 'Water Sidr Tree & Weed Base (+35 pts)',
    care_history: [
      { date: '2026-10-08', action: 'water', points: 30, notes: 'Deep root watering' },
    ],
  },
  {
    id: 'veg-02',
    name: 'Date Palm Grove #44',
    species: 'Date Palm (Phoenix dactylifera)',
    species_ar: 'نخلة التمر',
    lat: 31.628,
    lng: -7.985,
    district: 'Green Belt Commons',
    health: 'thriving',
    health_score: 92,
    ndvi: 0.68,
    planted_date: '2023-11-20',
    last_cared_at: 'Yesterday',
    photo: 'https://images.unsplash.com/photo-1509316975850-ff9c5deb0cd9?w=600&auto=format&fit=crop&q=80',
    adopted_by_me: true,
    mission_title: 'Clear ground debris & mulch (+40 pts)',
    care_history: [
      { date: '2026-10-10', action: 'clean', points: 40, notes: 'Cleared organic litter' },
    ],
  },
  {
    id: 'veg-03',
    name: 'Medina Olive Pocket Garden',
    species: 'Olive Tree (Olea europaea)',
    species_ar: 'شجرة الزيتون',
    lat: 31.639,
    lng: -7.989,
    district: 'Old Medina North',
    health: 'thriving',
    health_score: 85,
    ndvi: 0.61,
    planted_date: '2024-01-15',
    last_cared_at: 'Today',
    photo: 'https://images.unsplash.com/photo-1513836279014-a89f7a76ae86?w=600&auto=format&fit=crop&q=80',
    adopted_by_me: false,
    mission_title: 'Inspect for leaf borer & soil aeration (+25 pts)',
    care_history: [],
  },
  {
    id: 'veg-04',
    name: 'Bougainvillea Shading Pergola',
    species: 'Bougainvillea spectabilis',
    species_ar: 'جهنمية',
    lat: 31.621,
    lng: -8.005,
    district: 'Southern Urban Passage',
    health: 'thriving',
    health_score: 96,
    ndvi: 0.74,
    planted_date: '2024-05-10',
    last_cared_at: '2 days ago',
    photo: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=600&auto=format&fit=crop&q=80',
    adopted_by_me: false,
    mission_title: 'Trellis check & organic mulch (+35 pts)',
    care_history: [],
  },
  {
    id: 'veg-05',
    name: 'Citrus Lemon Courtyard',
    species: 'Meyer Lemon (Citrus limon)',
    species_ar: 'شجرة الليمون',
    lat: 31.643,
    lng: -8.012,
    district: 'West Residential Quarter',
    health: 'needs-water',
    health_score: 45,
    ndvi: 0.32,
    planted_date: '2024-06-01',
    last_cared_at: '5 days ago',
    photo: 'https://images.unsplash.com/photo-1463936575829-25148e1db1b8?w=600&auto=format&fit=crop&q=80',
    adopted_by_me: false,
    mission_title: 'Urgent: Water & add compost mulch (+50 pts)',
    care_history: [],
  },
  {
    id: 'veg-06',
    name: 'Atlas Cedar Urban Sapling',
    species: 'Atlas Cedar (Cedrus atlantica)',
    species_ar: 'أرز الأطلس',
    lat: 31.647,
    lng: -7.978,
    district: 'Botanical Conservatory Park',
    health: 'thriving',
    health_score: 90,
    ndvi: 0.65,
    planted_date: '2024-02-28',
    last_cared_at: 'Yesterday',
    photo: 'https://images.unsplash.com/photo-1448375240586-882707db888b?w=600&auto=format&fit=crop&q=80',
    adopted_by_me: true,
    mission_title: 'Check seedling stakes after wind (+25 pts)',
    care_history: [],
  },
]

export function useCitizenStore(initialUserName?: string) {
  // Load persistent profile
  const [profile, setProfile] = useState<CitizenProfileState>(() => {
    if (typeof window !== 'undefined') {
      const saved = window.localStorage.getItem('eco-citizen-profile')
      if (saved) {
        try {
          return { ...DEFAULT_PROFILE, ...JSON.parse(saved) }
        } catch {
          // ignore
        }
      }
    }
    return {
      ...DEFAULT_PROFILE,
      name: initialUserName || DEFAULT_PROFILE.name,
    }
  })

  // Load persistent vegetation list
  const [vegetationList, setVegetationList] = useState<LocalizedVegetation[]>(() => {
    if (typeof window !== 'undefined') {
      const saved = window.localStorage.getItem('eco-citizen-vegetation')
      if (saved) {
        try {
          return JSON.parse(saved)
        } catch {
          // ignore
        }
      }
    }
    return INITIAL_VEGETATION
  })

  const [selectedVegId, setSelectedVegId] = useState<string>('veg-01')
  const [toastMessage, setToastMessage] = useState<string | null>(null)
  const [claimedVouchers, setClaimedVouchers] = useState<ClaimedVoucher[]>(() => {
    if (typeof window !== 'undefined') {
      const saved = window.localStorage.getItem('eco-claimed-vouchers')
      if (saved) {
        try {
          return JSON.parse(saved)
        } catch {
          // ignore
        }
      }
    }
    return []
  })

  // Persist state
  const saveState = useCallback(
    (newProfile: CitizenProfileState, newVeg: LocalizedVegetation[], newVouchers?: ClaimedVoucher[]) => {
      setProfile(newProfile)
      setVegetationList(newVeg)
      if (newVouchers) setClaimedVouchers(newVouchers)
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('eco-citizen-profile', JSON.stringify(newProfile))
        window.localStorage.setItem('eco-citizen-vegetation', JSON.stringify(newVeg))
        if (newVouchers) {
          window.localStorage.setItem('eco-claimed-vouchers', JSON.stringify(newVouchers))
        }
        // Sync with root eco-user
        const rootUser = window.sessionStorage.getItem('eco-user')
        if (rootUser) {
          try {
            const parsed = JSON.parse(rootUser)
            window.sessionStorage.setItem(
              'eco-user',
              JSON.stringify({ ...parsed, points: newProfile.points, name: newProfile.name }),
            )
          } catch {
            // ignore
          }
        }
      }
    },
    [],
  )

  // Show toast with auto-dismiss
  const showToast = useCallback((msg: string) => {
    setToastMessage(msg)
    setTimeout(() => {
      setToastMessage(null)
    }, 4000)
  }, [])

  // Sync with live backend API on mount
  useEffect(() => {
    const fetchBackend = async () => {
      try {
        const [profRes, vegRes] = await Promise.all([
          fetch(`${API_BASE}/citizen/profile`, { signal: AbortSignal.timeout(3500) }).then((r) =>
            r.ok ? r.json() : null,
          ),
          fetch(`${API_BASE}/citizen/vegetation`, { signal: AbortSignal.timeout(3500) }).then((r) =>
            r.ok ? r.json() : null,
          ),
        ])

        if (profRes) {
          setProfile((prev) => ({
            ...prev,
            points: profRes.points ?? prev.points,
            streakDays: profRes.streak_days ?? prev.streakDays,
            monthlyGoal: profRes.monthly_goal ?? prev.monthlyGoal,
            completedMissions: profRes.completed_missions ?? prev.completedMissions,
            treesPlanted: profRes.trees_planted ?? prev.treesPlanted,
            wasteCollectedKg: profRes.waste_collected_kg ?? prev.wasteCollectedKg,
          }))
        }

        if (vegRes && Array.isArray(vegRes) && vegRes.length > 0) {
          setVegetationList(vegRes)
        }
      } catch {
        // Offline or backend timeout — gracefully continue with persistent storage
      }
    }
    fetchBackend()
  }, [])

  // 1. Log Care for Localized Plant
  const logCare = useCallback(
    async (plantId: string, action: 'water' | 'clean' | 'inspect' | 'fertilize' = 'water', notes?: string) => {
      const pts = action === 'water' ? 35 : action === 'fertilize' ? 40 : 25
      const now = new Date().toISOString().split('T')[0]

      const updatedVeg = vegetationList.map((p) => {
        if (p.id !== plantId) return p
        const newHist = [
          { date: now, action, points: pts, notes: notes || `Care action (${action}) completed` },
          ...(p.care_history || []),
        ]
        return {
          ...p,
          health: 'thriving' as const,
          health_score: Math.min(100, p.health_score + 18),
          ndvi: Math.min(0.85, +(p.ndvi + 0.05).toFixed(2)),
          last_cared_at: 'Just now',
          care_history: newHist,
        }
      })

      const updatedProfile: CitizenProfileState = {
        ...profile,
        points: profile.points + pts,
        treesPlanted: action === 'water' ? profile.treesPlanted : profile.treesPlanted,
      }

      saveState(updatedProfile, updatedVeg)
      const plant = vegetationList.find((p) => p.id === plantId)
      showToast(`💧 ${action === 'water' ? 'Watered' : 'Cared for'} ${plant?.name || 'plant'}! +${pts} EcoPoints earned.`)

      // Attempt async backend push
      try {
        fetch(`${API_BASE}/citizen/vegetation/${plantId}/care`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action, notes }),
        }).catch(() => {})
      } catch {
        // ignore
      }
    },
    [profile, vegetationList, saveState, showToast],
  )

  // 2. Localize New Plant
  const localizeNewPlant = useCallback(
    async (newPlant: {
      name: string
      species: string
      species_ar?: string
      lat: number
      lng: number
      district: string
      photo?: string
      notes?: string
    }) => {
      const id = `veg-${Date.now().toString(36)}`
      const now = new Date().toISOString().split('T')[0]
      const pts = 100

      const created: LocalizedVegetation = {
        id,
        name: newPlant.name,
        species: newPlant.species,
        species_ar: newPlant.species_ar || '',
        lat: newPlant.lat,
        lng: newPlant.lng,
        district: newPlant.district,
        health: 'thriving',
        health_score: 88,
        ndvi: 0.58,
        planted_date: now,
        last_cared_at: 'Just now',
        photo:
          newPlant.photo ||
          'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=600&auto=format&fit=crop&q=80',
        adopted_by_me: true,
        mission_title: 'Care for newly mapped vegetation (+35 pts)',
        care_history: [
          { date: now, action: 'planted', points: pts, notes: newPlant.notes || 'Mapped by citizen' },
        ],
      }

      const updatedVeg = [created, ...vegetationList]
      const updatedProfile: CitizenProfileState = {
        ...profile,
        points: profile.points + pts,
        treesPlanted: profile.treesPlanted + 1,
      }

      saveState(updatedProfile, updatedVeg)
      setSelectedVegId(id)
      showToast(`🌱 Localized "${created.name}" on map! +${pts} EcoPoints awarded.`)

      try {
        fetch(`${API_BASE}/citizen/vegetation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newPlant),
        }).catch(() => {})
      } catch {
        // ignore
      }
    },
    [profile, vegetationList, saveState, showToast],
  )

  // 3. Complete Mission
  const completeMission = useCallback(
    (missionTitle: string, pts = 100) => {
      const updatedProfile: CitizenProfileState = {
        ...profile,
        points: profile.points + pts,
        completedMissions: profile.completedMissions + 1,
        streakDays: profile.streakDays + 1,
      }
      saveState(updatedProfile, vegetationList)
      showToast(`🎉 Mission "${missionTitle}" completed! +${pts} EcoPoints earned.`)
    },
    [profile, vegetationList, saveState, showToast],
  )

  // 4. Update Monthly Goal
  const updateMonthlyGoal = useCallback(
    (goal: number) => {
      const updated: CitizenProfileState = { ...profile, monthlyGoal: goal }
      saveState(updated, vegetationList)
      showToast(`🎯 Monthly mission goal set to ${goal} missions.`)
      try {
        fetch(`${API_BASE}/citizen/profile`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ monthly_goal: goal }),
        }).catch(() => {})
      } catch {
        // ignore
      }
    },
    [profile, vegetationList, saveState, showToast],
  )

  // 5. Update Notification Preferences
  const updatePrefs = useCallback(
    (key: keyof CitizenProfileState['notificationPrefs'], val: boolean) => {
      const updated: CitizenProfileState = {
        ...profile,
        notificationPrefs: {
          ...profile.notificationPrefs,
          [key]: val,
        },
      }
      saveState(updated, vegetationList)
    },
    [profile, vegetationList, saveState],
  )

  // 6. Redeem Partner Reward
  const redeemReward = useCallback(
    async (offerId: string, partner: string, offerTitle: string, pointsCost: number) => {
      if (profile.points < pointsCost) {
        showToast(`❌ Insufficient EcoPoints (${profile.points} available, ${pointsCost} needed).`)
        return null
      }

      const code = `ECO-${partner.split(' ')[0].toUpperCase()}-${Math.floor(1000 + Math.random() * 9000)}`
      const newVoucher: ClaimedVoucher = {
        id: `vouch-${Date.now()}`,
        code,
        partner,
        offerTitle,
        pointsCost,
        claimedAt: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      }

      const updatedProfile: CitizenProfileState = {
        ...profile,
        points: profile.points - pointsCost,
      }
      const updatedVouchers = [newVoucher, ...claimedVouchers]

      saveState(updatedProfile, vegetationList, updatedVouchers)
      showToast(`🎁 Claimed "${offerTitle}"! Code: ${code}`)

      try {
        fetch(`${API_BASE}/citizen/rewards/redeem`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ offer_id: offerId, partner_name: partner, points_cost: pointsCost }),
        }).catch(() => {})
      } catch {
        // ignore
      }

      return newVoucher
    },
    [profile, vegetationList, claimedVouchers, saveState, showToast],
  )

  const selectedVegetation = vegetationList.find((v) => v.id === selectedVegId) || vegetationList[0]

  return {
    profile,
    vegetationList,
    selectedVegId,
    selectedVegetation,
    toastMessage,
    claimedVouchers,
    setSelectedVegId,
    logCare,
    localizeNewPlant,
    completeMission,
    updateMonthlyGoal,
    updatePrefs,
    redeemReward,
    showToast,
  }
}
